import { useEffect, useRef, useState } from "react";
import { MessageSquare, Send, X } from "lucide-react";

import { askAssistant } from "@/lib/chat.functions";
import { Button } from "@/components/ui/button";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const OPENER: Message = {
  role: "assistant",
  content:
    "Ask how working with Adkiro starts, what it costs and what access it needs — or anything about Meta Ads. I answer from what's published on this site.",
};

/**
 * What a business weighing Adkiro asks first, in the order they tend to ask.
 *
 * The first set — rising CPA, Advantage+ — were a practitioner's questions.
 * Someone deciding whether to hand over an ad account wants to know how it
 * starts, what it costs, whether the access is safe and who does the work,
 * and that is where the conversation should open. Every one is answered in
 * chat-knowledge.ts: a suggestion that ends in "I don't know" is worse than
 * none.
 */
const SUGGESTIONS = [
  "How do we get started?",
  "How much does it cost?",
  "How do I pay?",
  "What do you need from us?",
  "Is it safe to give you access?",
  "Do we need to get on a call?",
  "Who works on our account?",
  "Do you work with developers?",
  "How long is the contract?",
  "Which countries do you work with?",
];

/** Six when the chat opens; after each answer, the next three not yet asked. */
const FIRST_SHOWN = 6;
const FOLLOW_UPS = 3;

/** Renders bare paths like /guides/x as links, leaving the rest as text. */
function withLinks(text: string) {
  return text.split(/(\/[a-z0-9#][a-z0-9\-/#]*)/gi).map((part, i) =>
    /^\/[a-z0-9#]/i.test(part) ? (
      <a key={i} href={part} className="font-medium text-primary underline underline-offset-2">
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([OPENER]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const asked = new Set(messages.filter((m) => m.role === "user").map((m) => m.content));
  const suggestions = SUGGESTIONS.filter((s) => !asked.has(s)).slice(
    0,
    messages.length === 1 ? FIRST_SHOWN : FOLLOW_UPS,
  );

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: "end" });
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  async function send(text: string) {
    const question = text.trim();
    if (!question || busy) return;
    setInput("");
    setBusy(true);
    const next = [...messages, { role: "user" as const, content: question }];
    setMessages(next);
    try {
      const result = await askAssistant({
        data: {
          message: question,
          // Only the recent turns, and never the static opener.
          history: next.slice(1, -1).slice(-6),
        },
      });
      setMessages((m) => [...m, { role: "assistant", content: result.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Something went wrong. The guides at /guides may help, or request a review at /#audit.",
        },
      ]);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open the site assistant"
          className="chat-launcher fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 border border-border bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <MessageSquare className="size-4" />
          {/* On a phone only the icon shows: the words sat on top of the ads. */}
          <span className="chat-launcher-label">Ask a question</span>
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-label="Site assistant"
          className="fixed bottom-5 right-5 z-50 flex h-[min(32rem,calc(100vh-2.5rem))] w-[min(23rem,calc(100vw-2.5rem))] flex-col border border-border bg-background shadow-2xl"
        >
          <header className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <p className="text-sm font-semibold">Adkiro assistant</p>
              <p className="text-[0.68rem] text-muted-foreground">
                Automated · answers from this site
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close the assistant"
              className="p-1 text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2"
            >
              <X className="size-4" />
            </button>
          </header>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => (
              <div
                key={i}
                className={
                  m.role === "user"
                    ? "ml-auto max-w-[85%] bg-secondary px-3 py-2 text-sm"
                    : "max-w-[92%] text-sm leading-relaxed text-muted-foreground"
                }
              >
                {m.role === "assistant" ? withLinks(m.content) : m.content}
              </div>
            ))}

            {!busy && suggestions.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => void send(s)}
                    className="border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:border-primary hover:text-foreground"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {busy && <p className="text-sm text-muted-foreground">Thinking…</p>}
            <div ref={endRef} />
          </div>

          <form
            className="flex items-center gap-2 border-t border-border p-3"
            onSubmit={(e) => {
              e.preventDefault();
              void send(input);
            }}
          >
            <label htmlFor="assistant-input" className="sr-only">
              Your question
            </label>
            <input
              id="assistant-input"
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question…"
              maxLength={2000}
              autoComplete="off"
              className="min-w-0 flex-1 border border-border bg-background px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
            />
            <Button type="submit" variant="brand" size="sm" disabled={busy || !input.trim()}>
              <Send className="size-4" />
              <span className="sr-only">Send</span>
            </Button>
          </form>

          <p className="border-t border-border px-4 py-2 text-[0.62rem] leading-relaxed text-muted-foreground">
            Automated assistant. It can be wrong — nothing here is a quote or a guarantee.
          </p>
        </div>
      )}
    </>
  );
}
